import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Input, Label, Row } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Switch from "components/Shared/Switch"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import { changeLayout } from "store/actions"
import { Link, useNavigate } from "react-router-dom"
import CommonChangePassword from "./CommonChangePassword"
import { connect, useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { layoutTypes } from "constants/layout"
import { useTranslation } from "react-i18next"
import { formDataPut, get, put } from "helpers/api_helper"
import { showAlert } from "store/reducers/alertReducer"
import {
  DEFAULT_TENANT_BRANDING,
  applyTenantBranding,
  normalizeTenantBranding,
  saveTenantBrandingToStorage,
  BORDER_RADIUS_PRESETS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  TABLE_STYLE_OPTIONS,
  MIN_BORDER_RADIUS,
  MAX_BORDER_RADIUS,
} from "utils/tenantBranding"

const MyPreference = props => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { accountType, accountScope, authUser } = useSelector(state => state.Login || {})
  const isTenantSession =
    accountScope !== "super_admin" && accountType !== "superadmin"

  document.title = t("My Preference")

  const urlParams = new URLSearchParams(window.location.search)
  const tabValue = urlParams.get("tab")
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState(tabValue || "CHANGE_PASSWORD")
  const [themeLoading, setThemeLoading] = useState(false)
  const [themeSaving, setThemeSaving] = useState(false)
  const [themeForm, setThemeForm] = useState(DEFAULT_TENANT_BRANDING)
  const [logoUrlType, setLogoUrlType] = useState("URL")
  const [logoFile, setLogoFile] = useState(null)
  const [logoFilePreview, setLogoFilePreview] = useState("")

  const onChangeTabs = type => {
    setActiveTab(type)
  }

  const hasThemeChanges = useMemo(() => {
    const fromAuth = normalizeTenantBranding(authUser?.tenantBranding || {})
    const formSnapshot = normalizeTenantBranding(themeForm)
    if (JSON.stringify(formSnapshot) !== JSON.stringify(fromAuth)) return true
    return Boolean(logoFile)
  }, [authUser?.tenantBranding, themeForm, logoFile])

  const isAtDefaultTheme = useMemo(() => {
    const defaults = normalizeTenantBranding(DEFAULT_TENANT_BRANDING)
    const formSnapshot = normalizeTenantBranding(themeForm)
    return JSON.stringify(formSnapshot) === JSON.stringify(defaults) && !logoFile
  }, [themeForm, logoFile])

  const savedThemeFromAuth = useMemo(
    () => normalizeTenantBranding(authUser?.tenantBranding || {}),
    [authUser?.tenantBranding]
  )

  useEffect(() => {
    return () => {
      if (logoFilePreview) {
        URL.revokeObjectURL(logoFilePreview)
      }
    }
  }, [logoFilePreview])

  useEffect(() => {
    if (!isTenantSession) return

    let cancelled = false

    const fetchBranding = async () => {
      setThemeLoading(true)
      try {
        const response = await get("admin/tenants/branding")
        if (cancelled) return
        if (response?.status && response?.data) {
          const normalized = normalizeTenantBranding(response.data)
          setThemeForm(normalized)
          applyTenantBranding(normalized)
          saveTenantBrandingToStorage(normalized)
        }
      } catch (_err) {
        if (!cancelled) {
          dispatch(
            showAlert({
              text: t("Unable to fetch tenant theme settings"),
              type: "error",
            })
          )
        }
      } finally {
        if (!cancelled) {
          setThemeLoading(false)
        }
      }
    }

    fetchBranding()

    return () => {
      cancelled = true
    }
  }, [isTenantSession])

  const handleThemeValueChange = (section, key, value) => {
    setThemeForm(prev => {
      const draft = section
        ? {
            ...prev,
            [section]: {
              ...(prev?.[section] || {}),
              [key]: value,
            },
          }
        : {
            ...prev,
            [key]: value,
          }
      const next = normalizeTenantBranding(draft)
      applyTenantBranding(next)
      saveTenantBrandingToStorage(next)
      return next
    })
  }

  const handleTableToggleChange = (key, checked) => {
    setThemeForm(prev => {
      const next = normalizeTenantBranding({
        ...prev,
        tables: {
          ...(prev?.tables || {}),
          [key]: checked,
        },
      })
      applyTenantBranding(next)
      saveTenantBrandingToStorage(next)
      return next
    })
  }

  const resetLogoUploadState = () => {
    if (logoFilePreview) {
      URL.revokeObjectURL(logoFilePreview)
    }
    setLogoFile(null)
    setLogoFilePreview("")
    setLogoUrlType("URL")
  }

  const handleLogoTypeChange = type => {
    setLogoUrlType(type)
    if (type === "URL") {
      setLogoFile(null)
      if (logoFilePreview) {
        URL.revokeObjectURL(logoFilePreview)
        setLogoFilePreview("")
      }
    }
  }

  const handleLogoFileChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
    if (logoFilePreview) {
      URL.revokeObjectURL(logoFilePreview)
    }
    setLogoFile(file)
    setLogoFilePreview(URL.createObjectURL(file))
  }

  const logoPreviewSrc = logoFilePreview || themeForm.logoUrl || ""

  const handleSaveTenantTheme = async () => {
    const branding = normalizeTenantBranding(themeForm)

    if (logoUrlType === "UPLOAD" && !logoFile && !branding.logoUrl) {
      dispatch(
        showAlert({
          text: t("Please select a logo image to upload"),
          type: "error",
        })
      )
      return
    }

    setThemeSaving(true)
    try {
      let response
      if (logoUrlType === "UPLOAD" && logoFile) {
        const data = new FormData()
        data.append(
          "branding",
          JSON.stringify({
            sidebar: branding.sidebar,
            header: branding.header,
            buttons: branding.buttons,
            appearance: branding.appearance,
            tables: branding.tables,
            logoUrl: "",
          })
        )
        data.append("logoUrlType", "UPLOAD")
        data.append("image", logoFile)
        response = await formDataPut("admin/tenants/branding", data)
      } else {
        response = await put("admin/tenants/branding", { branding })
      }

      if (response?.status) {
        const normalized = normalizeTenantBranding(
          response?.data || branding
        )
        setThemeForm(normalized)
        resetLogoUploadState()
        saveTenantBrandingToStorage(normalized)
        applyTenantBranding(normalized)
        localStorage.setItem(
          "authUser",
          JSON.stringify({
            ...(authUser || {}),
            tenantBranding: normalized,
          })
        )
        dispatch(showAlert({ text: response?.msg || t("Saved successfully"), type: "success" }))
      } else {
        dispatch(showAlert({ text: response?.msg || t("Unable to save"), type: "error" }))
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to save"), type: "error" }))
    } finally {
      setThemeSaving(false)
    }
  }

  const handleCancelThemeChanges = () => {
    setThemeForm(savedThemeFromAuth)
    applyTenantBranding(savedThemeFromAuth)
    saveTenantBrandingToStorage(savedThemeFromAuth)
    resetLogoUploadState()
  }

  const handleResetThemeToDefaults = () => {
    const defaults = normalizeTenantBranding(DEFAULT_TENANT_BRANDING)
    setThemeForm(defaults)
    applyTenantBranding(defaults)
    saveTenantBrandingToStorage(defaults)
    resetLogoUploadState()
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />

        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle>{t("My Preference")}</CardTitle>

                <Row className="mt-3">
                  <Col>
                    <ul className="nav nav-pills" style={{ minWidth: 80 }}>
                      <li className="nav-item">
                        <Link
                          to="#"
                          className={`nav-link ${
                            activeTab === "CHANGE_PASSWORD" ? "active" : ""
                          }`}
                          onClick={() => {
                            onChangeTabs("CHANGE_PASSWORD")
                          }}
                        >
                          {t("Change Password")}
                        </Link>{" "}
                      </li>
                      {/* <li className="nav-item">
                        <Link
                          to="#"
                          className={`nav-link ${
                            activeTab === "CHANGE_LANGUAGE" ? "active" : ""
                          }`}
                          onClick={() => {
                            onChangeTabs("CHANGE_LANGUAGE")
                          }}
                        >
                          Change Language
                        </Link>{" "}
                      </li> */}
                      <li className="nav-item">
                        <Link
                          to="#"
                          className={`nav-link ${
                            activeTab === "CHANGE_LAYOUT" ? "active" : ""
                          }`}
                          onClick={() => {
                            onChangeTabs("CHANGE_LAYOUT")
                          }}
                        >
                          {t("Change Layout")}
                        </Link>{" "}
                      </li>
                      {isTenantSession && (
                        <li className="nav-item">
                          <Link
                            to="#"
                            className={`nav-link ${
                              activeTab === "THEME_LOGO" ? "active" : ""
                            }`}
                            onClick={() => {
                              onChangeTabs("THEME_LOGO")
                            }}
                          >
                            {t("Theme & Logo")}
                          </Link>{" "}
                        </li>
                      )}
                    </ul>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <hr />
                </Row>
                <Row>
                  {/* TAB CONTENT */}
                  {activeTab === "CHANGE_PASSWORD" && (
                    <Col lg={4}>
                      <div>
                        <CommonChangePassword />
                      </div>
                    </Col>
                  )}

                  {activeTab === "CHANGE_LAYOUT" && (
                    <Col lg={6}>
                      <div>
                        <Button
                          style={{
                            backgroundColor: `${
                              props.layoutType === layoutTypes.VERTICAL
                                ? "#556ee6"
                                : "#eff0f7"
                            }`,
                            border: "unset",
                            fontWeight: 600,
                            color: `${
                              props.layoutType === layoutTypes.VERTICAL
                                ? "#fff"
                                : "#495057"
                            }`,
                          }}
                          onClick={() => {
                            props.changeLayout(layoutTypes.VERTICAL)
                            navigate(`/my-preference?tab=CHANGE_LAYOUT`)
                          }}
                        >
                          {t("Vertical")}
                        </Button>
                        <Button
                          className="mx-2"
                          style={{
                            backgroundColor: `${
                              props.layoutType === layoutTypes.HORIZONTAL
                                ? "#556ee6"
                                : "#eff0f7"
                            }`,
                            border: "unset",
                            fontWeight: 600,
                            color: `${
                              props.layoutType === layoutTypes.HORIZONTAL
                                ? "#fff"
                                : "#495057"
                            }`,
                          }}
                          onClick={() => {
                            props.changeLayout(layoutTypes.HORIZONTAL)
                            navigate(`/my-preference?tab=CHANGE_LAYOUT`)
                          }}
                        >
                          {t("Horizontal")}
                        </Button>
                      </div>
                    </Col>
                  )}
                  {activeTab === "THEME_LOGO" && isTenantSession && (
                    <Col lg={6}>
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <Label>{t("Logo Type")}</Label>
                          <Input
                            type="select"
                            value={logoUrlType}
                            onChange={e => handleLogoTypeChange(e.target.value)}
                            disabled={themeLoading}
                          >
                            <option value="URL">{t("Image Url")}</option>
                            <option value="UPLOAD">{t("Upload Image")}</option>
                          </Input>
                        </div>
                        {logoUrlType === "URL" && (
                          <div>
                            <Label>{t("Logo URL")}</Label>
                            <Input
                              type="text"
                              placeholder="https://your-cdn.com/logo.png"
                              value={themeForm.logoUrl}
                              onChange={e =>
                                handleThemeValueChange(null, "logoUrl", e.target.value)
                              }
                              disabled={themeLoading}
                            />
                          </div>
                        )}
                        {logoUrlType === "UPLOAD" && (
                          <div>
                            <Label>{t("Upload Image")}</Label>
                            <TranslatedFileInput
                              accept="image/jpeg, image/jpg, image/png, image/webp"
                              onChange={handleLogoFileChange}
                              disabled={themeLoading}
                            />
                            <p className="pt-2 text-muted small mb-0">
                              {t("Accepted formats: JPG, JPEG, PNG")}
                            </p>
                          </div>
                        )}
                        {logoPreviewSrc && (
                          <div>
                            <Label>{t("Logo Preview")}</Label>
                            <div className="mt-2">
                              <img
                                src={logoPreviewSrc}
                                alt={t("Logo Preview")}
                                className="img_preview"
                                style={{ maxHeight: 120, maxWidth: 240 }}
                              />
                            </div>
                          </div>
                        )}
                        <Card className="mb-0">
                          <CardBody>
                            <CardTitle className="mb-3">{t("Appearance Section")}</CardTitle>
                            <p className="text-muted small mb-3">
                              {t(
                                "Controls roundness of buttons, inputs, badges, filters, and cards across the portal."
                              )}
                            </p>
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <Label className="mb-0">{t("Border Radius")}</Label>
                                  <span className="text-muted small">
                                    {themeForm.appearance?.borderRadius ?? 12}px
                                  </span>
                                </div>
                                <Input
                                  type="range"
                                  min={MIN_BORDER_RADIUS}
                                  max={MAX_BORDER_RADIUS}
                                  step={1}
                                  value={themeForm.appearance?.borderRadius ?? 12}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "appearance",
                                      "borderRadius",
                                      Number(e.target.value)
                                    )
                                  }
                                  disabled={themeLoading}
                                  className="form-range"
                                />
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {BORDER_RADIUS_PRESETS.map(preset => {
                                  const isActive =
                                    themeForm.appearance?.borderRadius === preset.value
                                  return (
                                    <Button
                                      key={preset.id}
                                      size="sm"
                                      color={isActive ? "primary" : "light"}
                                      active={isActive}
                                      onClick={() =>
                                        handleThemeValueChange(
                                          "appearance",
                                          "borderRadius",
                                          preset.value
                                        )
                                      }
                                      disabled={themeLoading}
                                    >
                                      {t(preset.labelKey)} ({preset.value}px)
                                    </Button>
                                  )
                                })}
                              </div>
                              <div>
                                <Label>{t("Font Family")}</Label>
                                <Input
                                  type="select"
                                  value={themeForm.appearance?.fontFamily || "inter"}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "appearance",
                                      "fontFamily",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                >
                                  {FONT_FAMILY_OPTIONS.map(option => (
                                    <option key={option.id} value={option.id}>
                                      {t(option.labelKey)}
                                    </option>
                                  ))}
                                </Input>
                              </div>
                              <div>
                                <Label>{t("Font Size")}</Label>
                                <Input
                                  type="select"
                                  value={themeForm.appearance?.fontSize ?? 14}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "appearance",
                                      "fontSize",
                                      Number(e.target.value)
                                    )
                                  }
                                  disabled={themeLoading}
                                >
                                  {FONT_SIZE_OPTIONS.map(option => (
                                    <option key={option.id} value={option.value}>
                                      {t(option.labelKey)} ({option.value}px)
                                    </option>
                                  ))}
                                </Input>
                              </div>
                              <div
                                className="p-3 border"
                                style={{
                                  borderRadius: `${
                                    themeForm.appearance?.borderRadius ?? 12
                                  }px`,
                                  backgroundColor: "var(--corp-surface)",
                                }}
                              >
                                <p className="text-muted small mb-2">{t("Live Preview")}</p>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    style={{
                                      borderRadius: `${
                                        themeForm.appearance?.borderRadius ?? 12
                                      }px`,
                                      pointerEvents: "none",
                                    }}
                                  >
                                    {t("Button")}
                                  </Button>
                                  <Input
                                    type="text"
                                    readOnly
                                    value={t("Input field")}
                                    style={{
                                      maxWidth: 160,
                                      borderRadius: `${
                                        themeForm.appearance?.borderRadius ?? 12
                                      }px`,
                                      pointerEvents: "none",
                                    }}
                                  />
                                  <span
                                    className="badge bg-primary"
                                    style={{
                                      borderRadius: "9999px",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    {t("Badge")}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <Card className="mb-0">
                          <CardBody>
                            <CardTitle className="mb-3">{t("Sidebar Section")}</CardTitle>
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <Label>{t("Sidebar Background")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.sidebar.backgroundColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "sidebar",
                                      "backgroundColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Sidebar Icon Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.sidebar.iconColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "sidebar",
                                      "iconColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Sidebar Menu Text Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.sidebar.menuTextColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "sidebar",
                                      "menuTextColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Sidebar Menu Hover Text Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.sidebar.menuTextHoverColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "sidebar",
                                      "menuTextHoverColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Sidebar Active Tab Background")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.sidebar.activeItemBackgroundColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "sidebar",
                                      "activeItemBackgroundColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Sidebar Active Tab Text Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.sidebar.activeItemTextColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "sidebar",
                                      "activeItemTextColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <Card className="mb-0">
                          <CardBody>
                            <CardTitle className="mb-3">{t("Header Section")}</CardTitle>
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <Label>{t("Header Background")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.header?.backgroundColor || "#FFFFFF"}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "header",
                                      "backgroundColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Header Text Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.header?.textColor || "#1E293B"}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "header",
                                      "textColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <Card className="mb-0">
                          <CardBody>
                            <CardTitle className="mb-3">{t("Tables Section")}</CardTitle>
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <Label>{t("Table Style")}</Label>
                                <Input
                                  type="select"
                                  value={themeForm.tables?.style || "default"}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "tables",
                                      "style",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                >
                                  {TABLE_STYLE_OPTIONS.map(option => (
                                    <option key={option.id} value={option.id}>
                                      {t(option.labelKey)}
                                    </option>
                                  ))}
                                </Input>
                              </div>
                              <div>
                                <Label>{t("Table Header Background")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.tables?.headerBackground || "#F8FAFC"}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "tables",
                                      "headerBackground",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div className="d-flex align-items-center justify-content-between gap-3">
                                <span>{t("Striped Table Rows")}</span>
                                <Switch
                                  isChecked={themeForm.tables?.striped === true}
                                  onClickHandler={() =>
                                    handleTableToggleChange(
                                      "striped",
                                      themeForm.tables?.striped !== true
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div className="d-flex align-items-center justify-content-between gap-3">
                                <span>{t("Highlight Row on Hover")}</span>
                                <Switch
                                  isChecked={themeForm.tables?.hoverHighlight === true}
                                  onClickHandler={() =>
                                    handleTableToggleChange(
                                      "hoverHighlight",
                                      themeForm.tables?.hoverHighlight !== true
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div className="table-responsive border rounded p-2">
                                <table className="table table-sm mb-0">
                                  <thead>
                                    <tr>
                                      <th>{t("Name")}</th>
                                      <th>{t("Status")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>{t("Sample row")}</td>
                                      <td>
                                        <span className="badge badge-soft-success">{t("Active")}</span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>{t("Another row")}</td>
                                      <td>
                                        <span className="badge badge-soft-warning">{t("Pending")}</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <Card className="mb-0">
                          <CardBody>
                            <CardTitle className="mb-3">{t("Buttons Section")}</CardTitle>
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <Label>{t("Primary Button Background")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.buttons.primaryBackgroundColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "buttons",
                                      "primaryBackgroundColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Primary Button Text Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.buttons.primaryTextColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "buttons",
                                      "primaryTextColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Secondary Button Background")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.buttons.secondaryBackgroundColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "buttons",
                                      "secondaryBackgroundColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                              <div>
                                <Label>{t("Secondary Button Text Color")}</Label>
                                <Input
                                  type="color"
                                  value={themeForm.buttons.secondaryTextColor}
                                  onChange={e =>
                                    handleThemeValueChange(
                                      "buttons",
                                      "secondaryTextColor",
                                      e.target.value
                                    )
                                  }
                                  disabled={themeLoading}
                                />
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            color="success"
                            onClick={handleSaveTenantTheme}
                            disabled={themeSaving || themeLoading || !hasThemeChanges}
                          >
                            {t("Save")}
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            onClick={handleCancelThemeChanges}
                            disabled={themeSaving || themeLoading || !hasThemeChanges}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            color="warning"
                            outline
                            onClick={handleResetThemeToDefaults}
                            disabled={themeSaving || themeLoading || isAtDefaultTheme}
                          >
                            {t("Reset to Defaults")}
                          </Button>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>

                {/* <div className="mt-4">
                  <Button
                    color="primary"
                    onClick={() => showRightSidebarAction(true)}
                  >
                    Change Setting
                  </Button>
                </div> */}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

// export default MyPreference

MyPreference.propTypes = {
  changeLayout: PropTypes.func,
}

const mapStateToProps = state => {
  return { ...state.Layout }
}

export default connect(mapStateToProps, {
  changeLayout,
})(MyPreference)
